import type { Assignment, Tactic, Utility } from "@cs2/contracts";

/**
 * Erste Übereinstimmung, sonst der erste Eintrag der Liste — die generische
 * Form der `.find(...) ?? array[0]`-Fallback-Kette, die in Editor und
 * Quickview unabhängig voneinander für Map-/Taktik-/Zuweisungsauswahl
 * entstanden ist.
 */
export function findOrFirst<T>(
  items: readonly T[],
  predicate: (item: T) => boolean,
): T | undefined {
  return items.find(predicate) ?? items[0];
}

/**
 * Wählt die aktive Taktik aus den für Karte+Seite bereits gefilterten
 * Kandidaten. Kein Fallback auf "irgendeine Taktik dieser Karte, egal welche
 * Seite" — das würde beim Wechsel auf eine Seite ohne eigene Taktik die alte
 * Seite weiter anzeigen und im Editor den Auto-Anlage-Effekt nie greifen
 * lassen. Gibt `candidates` nichts her, bleibt das Ergebnis bewusst
 * `undefined` — genau wie bei einer frisch gewählten leeren Karte.
 */
export function resolveActiveTactic(
  candidates: readonly Tactic[],
  tacticId: string,
): Tactic | undefined {
  return findOrFirst(candidates, (item) => item.id === tacticId);
}

/** Wählt die Zuweisung für einen Slot, sonst die erste vorhandene. */
export function resolveAssignment(
  assignments: readonly Assignment[],
  slot: string,
): Assignment | undefined {
  return findOrFirst(assignments, (item) => item.slot === slot);
}

/** Eigene und Team-Utilities einer Zuweisung, dedupliziert. */
export function resolveAssignedUtilities(
  assignment: Assignment,
  tactic: Tactic,
  utilityById: Map<string, Utility>,
): Utility[] {
  return [...new Set([...assignment.utilityIds, ...tactic.teamUtilityIds])]
    .map((id) => utilityById.get(id))
    .filter((item): item is Utility => Boolean(item));
}

/** Utilities, die einem bestimmten Schritt einer Zuweisung zugeordnet sind. */
export function utilitiesForStep(
  utilities: readonly Utility[],
  assignment: Assignment,
  stepOrder: number,
): Utility[] {
  return utilities.filter(
    (utility) => assignment.utilityStepLinks[utility.id] === stepOrder,
  );
}
